import { DotHeadless } from "@dotdev/dotheadless-app"

type BrazeClient = typeof import("@app/lib/braze.client")

type InitBrazeOptions = {
  apiKey?: string
  apiUrl?: string
  market?: string
}

// `gatsby develop` is the only environment where GATSBY_ACTIVE_ENV/NODE_ENV are "development";
// every built deploy (staging included) runs as "production", so staging keeps sending for real.
export const isBrazeDev = DotHeadless.helpers.isDev

// Central dev logger. Every Braze call site logs its event name and payload here instead of
// sending, mirroring how the GTM provider console.logs the dataLayer rather than pushing it.
export const logSkipped = (eventName: string, payload?: unknown): void => {
  if (!isBrazeDev) return
  console.log(`[Braze] ${eventName} skipped (local dev)`, payload)
}

// `window.braze` hands any script on the page changeUser and wipeData against live customer
// profiles, so the handle and SDK logging stay on for `gatsby develop`, Netlify deploy previews and
// branch deploys (`--` in the hostname), and staging (staging.allkinds.com, see
// config.allkinds-staging.js) only. Production sites' default Netlify domains (e.g.
// allkinds-production-au.netlify.app) serve the live build, so they are treated as live. Every other
// host is treated as the live storefront, so an unknown host fails safe.
const isDebugHost = (): boolean => {
  const { hostname } = window.location
  return (
    isBrazeDev ||
    hostname === "localhost" ||
    (hostname.endsWith(".netlify.app") && hostname.includes("--")) ||
    hostname === "staging.allkinds.com"
  )
}

let readyPromise: Promise<BrazeClient> | null = null

export const initBraze = ({ apiKey, apiUrl, market }: InitBrazeOptions): Promise<BrazeClient> | null => {
  if (typeof window === "undefined") return null
  if (!apiKey || !apiUrl) return null
  if (readyPromise) return readyPromise

  readyPromise = import("@app/lib/braze.client").then(braze => {
    const debugHost = isDebugHost()
    if (debugHost) (window as unknown as { braze: typeof braze }).braze = braze
    if (isBrazeDev) {
      logSkipped("initialize", { apiUrl, market })
      return braze
    }
    if (!braze.isInitialized()) {
      braze.initialize(apiKey, {
        baseUrl: apiUrl,
        sessionTimeoutInSeconds: 1800,
        enableLogging: debugHost,
        allowUserSuppliedJavascript: true,
        doNotLoadFontAwesome: true,
        contentSecurityNonce: undefined,
      })
      braze.openSession()
      if (market) braze.getUser()?.setCustomUserAttribute("market", market)
      braze.requestImmediateDataFlush()
      braze.automaticallyShowInAppMessages()
      braze.requestContentCardsRefresh()
    }
    return braze
  })

  return readyPromise
}

export const getBrazeWhenReady = (): Promise<BrazeClient> | null => {
  if (typeof window === "undefined") return null
  return readyPromise
}
